import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { User } from 'app/models/user';
import { UserService } from './user.service';
import { LogService } from './log.service';
import { Organization, OrganizationCourse } from 'app/models/organization';
import { AnalyticEvent } from './analytic-event';
import { Reads, voice } from 'app/models/course';
import { ModalService } from './modal.service';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, shareReplay } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class SeriesService {
    private categoriesSubject = new BehaviorSubject<any[]>([]);
    private videoSeriesSubject = new BehaviorSubject<any>({});
    private voiceStorySeriesSubject = new BehaviorSubject<any>({});
    private voiceBurningQuestionSeriesSubject = new BehaviorSubject<any>({});
    private dailySeriesSubject = new BehaviorSubject<any>({});
    categories = this.categoriesSubject.asObservable();
    videoSeries = this.videoSeriesSubject.asObservable();
    voiceStorySeries = this.voiceStorySeriesSubject.asObservable();
    voiceBurningQuestionSeries =
        this.voiceBurningQuestionSeriesSubject.asObservable();
    dailySeriesContent = this.dailySeriesSubject.asObservable();
    user: User;
    popups: any;

    constructor(
        private api: ApiService,
        private userService: UserService,
        private log: LogService,
        private modalService: ModalService,
        private translate: TranslateService
    ) {
        this.user = this.userService.getUser();
        this.translate.stream('series').subscribe((res: any) => {
            this.popups = res.popups;
        });
    }
    getCategories() {
        let user: User = this.userService.getUser();
        if (this.categoriesSubject.value.length === 0) {
            this.api.get('series/categories').subscribe(
                (result: any) => {
                    this.log.event(
                        AnalyticEvent.internalPagesEvents.categoryListLoaded
                    );
                    let categoryList = [
                        {
                            ID: 0,
                            Key: 'all',
                            Title: 'All',
                            isSelected: true,
                            hasVideos: 1,
                            hasStories: 1,
                            hasBurningQuestions: 1,
                        },
                        ...result.map(category => ({
                            ...category,
                            isSelected: false,
                            hasVideos: 0,
                        })),
                    ];
                    if (
                        user.primaryOrganization &&
                        user.primaryOrganization.id
                    ) {
                        categoryList = this.setVoiceCategoryList(
                            user.primaryOrganization.storiesConfig,
                            user.primaryOrganization.burningQuestionsConfig,
                            categoryList
                        );
                    }

                    this.checkHasVideoSeriesEnabled(categoryList);
                },
                error => {
                    this.log.event(
                        AnalyticEvent.internalPagesEvents.categoryListFetchError
                    );
                    this.categoriesSubject.next([]);
                }
            );
        }
    }
    checkHasVideoSeriesEnabled(categoryList) {
        let user: User = this.userService.getUser();
        if (user.primaryOrganization && user.primaryOrganization.id) {
            const courses = user.primaryOrganization.courses;
            if (courses !== undefined && courses.length > 0) {
                let courseList = this.setVideoSeriesPermissions(courses);
                this.setVideoCategoryList(courseList, categoryList);
            } else {
                this.fetchDefaultCoursesForCategory(categoryList);
            }
        } else {
            this.fetchDefaultCoursesForCategory(categoryList);
        }
    }

    checkHasVoiceSeriesEnabled(categoryList) {
        let user: User = this.userService.getUser();
        if (user.primaryOrganization && user.primaryOrganization.id) {
        }
    }

    setVideoCategoryList(courseList: any, categoryList: any) {
        const usedCategoryIds = new Set(
            Object.values(courseList).map(
                (course: any) => course.Category || course.category
            )
        );
        const updatedCategory = categoryList.map(category => ({
            ...category,
            hasVideos: usedCategoryIds.has(category.ID) ? 1 : 0,
        }));
        this.categoriesSubject.next(updatedCategory);
    }

    setVoiceCategoryList(
        storyList: any,
        burningQuestionList: any,
        categoryList: any
    ) {
        const storyCategoryIds = new Set(
            Object.values(storyList)
                .filter((story: any) => story.active && story.enabled)
                .map((story: any) => story.Category || story.category)
        );
        const bqCategoryIds = new Set(
            Object.values(burningQuestionList)
                .filter((bq: any) => bq.active && bq.enabled)
                .map((bq: any) => bq.Category || bq.category)
        );
        let updatedCategory = categoryList.map(category => ({
            ...category,
            hasStories:
                storyCategoryIds.has(category.ID) || category.Key == 'all'
                    ? 1
                    : 0,
            hasBurningQuestions:
                bqCategoryIds.has(category.ID) || category.Key == 'all' ? 1 : 0,
        }));

        return updatedCategory;
    }

    private fetchDefaultCoursesForCategory(categoryList: any[]) {
        this.api.get('series/serieslisting/defaults').subscribe({
            next: (result: any) => {
                if (result?.data) {
                    const defaultCourses =
                        result?.data?.filter(course => course.DefaultCourse) ??
                        [];
                    this.setVideoCategoryList(defaultCourses, categoryList);
                } else {
                    this.categoriesSubject.next(categoryList);
                }
            },
            error: () => {
                this.categoriesSubject.next(categoryList);
            },
        });
    }
    getCourses(
        showLoader: boolean = true,
        showError: boolean = true,
        reload: boolean = false
    ) {
        if (
            Object.keys(this.videoSeriesSubject.value).length === 0 ||
            this.videoSeriesSubject.value?.seriesList.length === 0 ||
            reload
        ) {
            let organization: Organization;
            if (
                this.user.primaryOrganization &&
                this.user.primaryOrganization.id
            ) {
                this.api
                    .get('organizations/' + this.user.primaryOrganization.id, {
                        fetchcourseimg: true,
                    })
                    .subscribe(
                        (result: any) => {
                            organization = new Organization(
                                result.data,
                                'full'
                            );
                            if (
                                organization.courses !== undefined &&
                                organization.courses.length > 0
                            ) {
                                this.unlock(organization.courses);
                            } else {
                                this.loadDefaultCourses(showLoader);
                            }

                            if (
                                organization.integrations &&
                                organization.integrations.togetherAllSsoLink
                            ) {
                                let ssoLink =
                                    organization.integrations
                                        .togetherAllSsoLink;
                                localStorage.setItem('ssoLink', ssoLink);
                            } else {
                                localStorage.removeItem('ssoLink');
                            }
                        },
                        (error: any) => {
                            this.log.event(
                                AnalyticEvent.internalPagesEvents
                                    .videoListFetchError
                            );
                            this.setVideoSeries('error', []);
                            // showError &&
                            //   this.errorService.showAlert(
                            //     this.popupText.alert,
                            //     this.popupText.errorGettingSeries,
                            //     this.popupText.ok
                            //   );
                            this.log.error(
                                'Error getting series. ' + error.message
                            );
                            // showLoader && this.loader.close();
                        }
                    );
            } else {
                this.unlock();
            }
        }
    }
    unlock(enabledCourses?: any) {
        if (enabledCourses && enabledCourses.length > 0) {
            this.log.event(AnalyticEvent.internalPagesEvents.videoListLoaded);
            this.setVideoSeries('success', enabledCourses);
        } else {
            this.loadDefaultCourses();
        }
    }
    setVideoSeriesPermissions(courses) {
        let courseList = {};
        for (const course of courses) {
            if (course && course['enabled']) {
                courseList[course.key] = course;
            }
        }
        if (
            courseList['disaster']?.['enabled'] &&
            courseList['resilience']?.['enabled'] &&
            courseList['resiliences']?.['enabled']
        ) {
            courseList['resiliences']['enabled'] = true;
            courseList['disaster']['enabled'] = false;
            courseList['resilience']['enabled'] = false;
        } else if (
            courseList['disaster']?.['enabled'] &&
            !courseList['resilience']?.['enabled']
        ) {
            courseList['disaster']['enabled'] = true;
        } else if (
            courseList['resilience']?.['enabled'] &&
            !courseList['disaster']?.['enabled']
        ) {
            courseList['resilience']['enabled'] = true;
        }
        return courseList;
    }

    loadDefaultCourses(showLoader: boolean = true, showError: boolean = true) {
        // showLoader && this.loader.show();
        let defaultCourses = [];
        this.api.get('series/serieslisting/defaults').subscribe(
            (result: any) => {
                if (result.data) {
                    this.log.event(
                        AnalyticEvent.internalPagesEvents.videoListDefaultLoaded
                    );
                    // showLoader && this.loader.close();
                    result.data.forEach(course => {
                        if (course.DefaultCourse && course.Default) {
                            defaultCourses.push(
                                new OrganizationCourse({
                                    id: course.ID,
                                    courseId: course.ID,
                                    key: course.CourseKey,
                                    name: course.Name,
                                    description: course.Description,
                                    enabled: course.DefaultCourse,
                                    videoCount: course.videoCount,
                                    length: course.Length,
                                    courseImage: course.CourseImage,
                                    completed: course.completed,
                                })
                            );
                        }
                    });
                    this.setVideoSeries('success', defaultCourses);
                }
            },
            (error: any) => {
                this.log.event(
                    AnalyticEvent.internalPagesEvents.videoListDefaultFetchError
                );
                this.setVideoSeries('error', []);
                // showError &&
                //   this.errorService.showAlert(
                //     this.popupText.alert,
                //     this.popupText.errorGettingSeries,
                //     this.popupText.ok
                //   );
                this.log.error('Error getting series. ' + error.message);
                // showLoader && this.loader.close();
            }
        );
    }
    setVideoSeries(status: string, seriesList: any) {
        if (status == 'success' && !Object.keys(seriesList).length) {
            // this.errorService.showAlert(
            //   this.popupText.alert,
            //   this.popupText.emptyOrgVideoSeriesMsg,
            //   this.popupText.ok
            // );
        }
        const data = {
            status,
            seriesList,
        };
        this.videoSeriesSubject.next(data);
    }
    getVoiceStorySeries() {
        if (
            Object.keys(this.voiceStorySeriesSubject.value).length === 0 ||
            this.voiceStorySeriesSubject.value?.seriesList.length === 0
        ) {
            // this.loader.show();
            this.api.get('series/stories').subscribe(
                (result: any) => {
                    const seriesList = this.user?.primaryOrganization
                        ? this.getEnabledVoiceSeries(
                              result,
                              this.user?.primaryOrganization.storiesConfig
                          )
                        : result?.filter((def: any) => def?.Active) ?? [];
                    // this.loader.close();
                    this.log.event(
                        AnalyticEvent.internalPagesEvents.storiesListLoaded
                    );
                    this.setVoiceStorySeries('success', seriesList);
                },
                (error: any) => {
                    this.log.event(
                        AnalyticEvent.internalPagesEvents.storiesListFetchError
                    );
                    this.setVoiceStorySeries('error', []);
                    this.log.error('Error getting series. ' + error.message);
                }
            );
        }
    }

    getEnabledVoiceSeries(seriesList: any[], config: any): any[] {
        const primaryOrganizationSeriesMap = config?.reduce(
            (acc, item: any) => {
                acc[item.seriesId] = item;
                return acc;
            },
            {}
        );
        const newArray: any[] =
            seriesList?.filter(
                (def: any) =>
                    primaryOrganizationSeriesMap[def.ID]?.active &&
                    primaryOrganizationSeriesMap[def.ID]?.enabled
            ) ?? [];
        return newArray;
    }

    setVoiceStorySeries(status: string, seriesList: any) {
        if (status == 'success' && !Object.keys(seriesList).length) {
            // this.errorService.showAlert(
            //   this.popupText.alert,
            //   this.popupText.emptyOrgVoiceSeriesMsg,
            //   this.popupText.ok
            // );
        }
        const data = {
            status,
            seriesList,
        };
        this.voiceStorySeriesSubject.next(data);
    }

    getBurningQuestionsSeries() {
        if (
            Object.keys(this.voiceBurningQuestionSeriesSubject.value).length ===
                0 ||
            this.voiceBurningQuestionSeriesSubject.value?.seriesList.length ===
                0
        ) {
            // this.loader.show();
            this.api.get('series/burningquestions').subscribe(
                (result: any) => {
                    const seriesList = this.user?.primaryOrganization
                        ? this.getEnabledVoiceSeries(
                              result,
                              this.user?.primaryOrganization
                                  .burningQuestionsConfig
                          )
                        : result?.filter((def: any) => def?.Active) ?? [];
                    this.log.event(
                        AnalyticEvent.internalPagesEvents
                            .burningQuestionsListLoaded
                    );
                    // this.loader.close();
                    this.setBurningQuestionSeries('success', seriesList);
                },
                (error: any) => {
                    this.log.event(
                        AnalyticEvent.internalPagesEvents
                            .burningQuestionsListFetchError
                    );
                    this.setBurningQuestionSeries('error', []);
                    this.log.error('Error getting series. ' + error.message);
                }
            );
        }
    }

    setBurningQuestionSeries(status: string, seriesList: any) {
        if (status == 'success' && !Object.keys(seriesList).length) {
            // this.errorService.showAlert(
            //   this.popupText.alert,
            //   this.popupText.emptyOrgVoiceSeriesMsg,
            //   this.popupText.ok
            // );
        }
        const data = {
            status,
            seriesList,
        };
        this.voiceBurningQuestionSeriesSubject.next(data);
    }

    updateAudioProgress(progressData: any, audio: voice, type: string) {
        let time = progressData.current;
        let status =
            Object.keys(audio.courseProgress).length > 0
                ? audio.courseProgress.Status
                : 'inprogress';
        // if (status == 'inprogress' && progressData.left <= 10000) {
        //     status = 'completed';
        //     time = 0;
        //     audio.courseProgress['Status'] = status;
        // }
        let payload = {
            ID: audio.id,
            Status: status,
            Time: time.toString(),
        };
        let url: string =
            type == 'stories'
                ? 'series/story/updateprogress'
                : 'series/burningquestion/updateprogress';
        this.api.post(url, payload).subscribe(
            (result: any) => {
                this.log.debug('Course progress saved successfully.');
                audio.courseProgress = payload;
                this.updateSeriesData(type, audio, 'Progress', payload);
            },
            (error: any) => {
                this.log.error('Error issuing callback. ');
            }
        );
    }

    rateAudioSeries(rating: any, audio: voice, type: string) {
        let url: string =
            type == 'stories'
                ? 'series/story/feedback'
                : 'series/burningquestion/feedback';
        // this.loader.show();
        this.api
            .post(url, {
                rating: rating,
                AudioID: audio.id,
            })
            .subscribe(
                (result: any) => {
                    this.log.event(
                        AnalyticEvent.internalPagesEvents.seriesRatingAdded
                    );
                    audio.rating = rating;
                    this.modalService.showAlert(
                        this.popups[0].title,
                        this.popups[0].body
                    );
                    this.updateSeriesData(type, audio, 'Rating', result);
                },
                (error: any) => {
                    this.log.event(
                        AnalyticEvent.internalPagesEvents
                            .seriesRatingSubmitError
                    );
                    this.modalService.showAlert(
                        this.popups[1].title,
                        this.popups[1].body
                    );
                }
            );
    }

    checkSeriesTypesEnabled() {
        let enableStorySeries: boolean = true;
        let enableBurningQuestionSeries: boolean = true;
        let enableDailySeries: boolean = true;
        if (
            this.user?.primaryOrganization &&
            this.user.primaryOrganization?.id
        ) {
            enableStorySeries = this.user.primaryOrganization.enableStory;
            enableBurningQuestionSeries =
                this.user.primaryOrganization.enableBurningQuestion;
            enableDailySeries = this.user.primaryOrganization.enableDailySeries;
        }
        return {
            enableStorySeries,
            enableBurningQuestionSeries,
            enableDailySeries,
        };
    }
    updateSeriesData(seriesType: string, data: any, key: string, value: any) {
        switch (seriesType) {
            case 'stories': {
                const currentData = this.voiceStorySeriesSubject.value;
                const updatedSeriesList = currentData.seriesList.map(item =>
                    item.ID === data.id ? { ...item, [key]: value } : item
                );
                currentData.seriesList = updatedSeriesList;
                this.voiceStorySeriesSubject.next(currentData);
                break;
            }
            case 'burningQuestions': {
                const currentData =
                    this.voiceBurningQuestionSeriesSubject.value;
                const updatedSeriesList = currentData.seriesList.map(item =>
                    item.ID === data.id ? { ...item, [key]: value } : item
                );
                currentData.seriesList = updatedSeriesList;
                this.voiceBurningQuestionSeriesSubject.next(currentData);
                break;
            }
        }
    }
    getDailySeries(showLoader: boolean = false) {
        if (Object.keys(this.dailySeriesSubject.value).length === 0) {
            //   showLoader && this.loader.show();
            this.api.get('series/dailyseries/today').subscribe(
                (result: any) => {
                    this.log.event(
                        AnalyticEvent.internalPagesEvents.dailyContentLoaded
                    );
                    this.dailySeriesSubject.next(result);
                },
                error => {
                    this.log.error(
                        'Error getting daily content. ' + error.message
                    );
                    this.log.event(
                        AnalyticEvent.internalPagesEvents.dailyContentFetchError
                    );
                    //   showLoader && this.loader.close();
                    this.dailySeriesSubject.next({});
                }
            );
        }
    }
    getOrgCustiomVideoSeriesList(orgID: any): Observable<any> {
    return this.api.get(`series/ownseries`).pipe(
      map((response: any) => {
        let seriesList = OrganizationCourse.initializeArray(
          response?.data[0] || [],
        );

        return seriesList;
      }),
      catchError(() => {
        this.log.event(
          AnalyticEvent.internalPagesEvents.customOrgVideoSeriesFetchError,
        );
        return of([]);
      }),
      shareReplay(1),
    );
  }
  getOrgCustiomReadableSeriesList(orgID: any): Observable<any> {
    return this.api.get(`series/reads`).pipe(
      map((response: any) => {
        let seriesList = Reads.initializeArray(response || []);
        return seriesList;
      }),
      catchError(() => {
        this.log.event(
          AnalyticEvent.internalPagesEvents.customOrgReadsSeriesFetchError,
        );
        return of([]);
      }),
      shareReplay(1),
    );
  }
}
